import {
  GradeLevel,
  ProjectFormData,
  CreateProjectResponse,
  SelectedSkillStandard,
  ProjectApiResponse
} from '../types'
import { transformToApiPayload, transformToUpdatePayload, validatePayload, transformApiResponseToFormData } from '../utils'
import { createProject, getProject, showSelectedItems, updateProject } from 'pages/NewProject/service/projects'
import { api } from 'helpers'
import {
  updateSelectedSkillsStandards,
  getSkillsStandards
} from 'pages/NewProject/service/SkillsStandardsService'

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

interface IProjectService {
  getGradeLevels(): Promise<GradeLevel[]>
  createProjectWithAI(data: ProjectFormData): Promise<CreateProjectResponse>
  createDraftProject(data: ProjectFormData): Promise<CreateProjectResponse>
  updateProjectDetails(projectId: string, data: ProjectFormData): Promise<CreateProjectResponse>
  saveProjectDraft(data: ProjectFormData): void
  updateProjectSkills(projectId: string, skills: SelectedSkillStandard[]): Promise<void>
  getProjectCurriculum(projectId: string): Promise<unknown>
  getProjectForEdit(projectId: string): Promise<Partial<ProjectFormData> | null>
}

/**
 * Service for Arc Mosaico project operations
 */
class ProjectService implements IProjectService {
  /**
   * Fetches available grade levels from Arc Mosaico API
   * @returns Array of grade levels with id and name from US education system
   */
  async getGradeLevels(): Promise<GradeLevel[]> {
    try {
      const response = await api.get<Array<{ id: string; name: string }>>(
        '/api/arc_mosaico/grades'
      )
      return response.data.map(grade => ({
        value: grade.id,
        label: grade.name
      }))
    } catch (error) {
      console.error('Error fetching grade levels:', error)
      return []
    }
  }

  /**
   * Creates a project using AI generation (placeholder for future integration)
   * @param data - Form data for project creation
   * @returns Response with created project info
   */
  async createProjectWithAI(data: ProjectFormData): Promise<CreateProjectResponse> {
    await delay(15000)

    return {
      success: true,
      data: {
        projectId: `project-${Date.now()}`,
        status: 'draft',
        createdAt: new Date().toISOString()
      }
    }
  }

  /**
   * Creates a draft project using the existing projects API
   * @param data - Form data for project creation
   * @returns Response with created project ID or error details
   */
  async createDraftProject(data: ProjectFormData): Promise<CreateProjectResponse> {
    try {
      const validation = validatePayload(data)
      if (!validation.valid) {
        return {
          success: false,
          data: {
            projectId: '',
            status: 'draft',
            createdAt: new Date().toISOString()
          },
          error: {
            code: 'VALIDATION_ERROR',
            message: validation.errors.join(', '),
            fields: validation.errors
          }
        }
      }

      const payload = transformToApiPayload(data)
      const response = await createProject(payload as any)

      if (!response?.id) {
        return {
          success: false,
          data: {
            projectId: '',
            status: 'draft',
            createdAt: new Date().toISOString()
          },
          error: {
            code: 'CREATE_ERROR',
            message: 'Erro ao criar projeto'
          }
        }
      }

      if (data.skillsAndStandards && data.skillsAndStandards.length > 0) {
        try {
          const itemIds = data.skillsAndStandards.map(item => item.id)
          await updateSelectedSkillsStandards(response.id, {
            lv4_item_ids: itemIds
          })
        } catch (skillsError) {
          console.warn('Skills update failed:', skillsError)
        }
      }

      return {
        success: true,
        data: {
          projectId: response.id,
          status: 'draft',
          createdAt: new Date().toISOString()
        }
      }

    } catch (error) {
      return {
        success: false,
        data: {
          projectId: '',
          status: 'draft',
          createdAt: new Date().toISOString()
        },
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Erro desconhecido'
        }
      }
    }
  }

  /**
   * Updates an existing project's details (edit mode)
   * @param projectId - ID of the project to update
   * @param data - Updated form data
   * @returns Response with updated project ID or error details
   * @remarks Does not send grades/kind to avoid curriculum validation issues
   */
  async updateProjectDetails(projectId: string, data: ProjectFormData): Promise<CreateProjectResponse> {
    try {
      if (!projectId) {
        return {
          success: false,
          data: {
            projectId: '',
            status: 'draft',
            createdAt: new Date().toISOString()
          },
          error: {
            code: 'MISSING_PROJECT_ID',
            message: 'Project ID is required for update'
          }
        }
      }

      const payload = transformToUpdatePayload(data)
      const response = (await updateProject(projectId, payload as any)) as { id?: string }

      if (!response?.id) {
        return {
          success: false,
          data: {
            projectId,
            status: 'draft',
            createdAt: new Date().toISOString()
          },
          error: {
            code: 'UPDATE_ERROR',
            message: 'Erro ao atualizar projeto'
          }
        }
      }

      return {
        success: true,
        data: {
          projectId: response.id,
          status: 'draft',
          createdAt: new Date().toISOString()
        }
      }

    } catch (error) {
      return {
        success: false,
        data: {
          projectId,
          status: 'draft',
          createdAt: new Date().toISOString()
        },
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Erro desconhecido'
        }
      }
    }
  }

  /**
   * Saves project draft to localStorage
   * @param data - Form data to persist
   */
  saveProjectDraft(data: ProjectFormData): void {
    localStorage.setItem('arc_project_draft', JSON.stringify(data))
  }

  /**
   * Updates skills and standards for an existing project
   * @param projectId - ID of the project to update
   * @param skills - Array of selected skill standards
   * @throws Error if projectId is not provided
   */
  async updateProjectSkills(projectId: string, skills: SelectedSkillStandard[]): Promise<void> {
    if (!projectId) {
      throw new Error('Project ID is required')
    }

    const itemIds = skills.map(item => item.id)
    await updateSelectedSkillsStandards(projectId, {
      lv4_item_ids: itemIds
    })
  }

  /**
   * Loads available curriculum for a project
   * @param projectId - ID of the project
   * @returns Curriculum data from skills_and_contents endpoint
   * @throws Error if projectId is not provided
   */
  async getProjectCurriculum(projectId: string): Promise<unknown> {
    if (!projectId) {
      throw new Error('Project ID is required')
    }

    return await getSkillsStandards(projectId)
  }

  /**
   * Loads project data for editing
   * @param projectId - ID of the project to load
   * @returns Partial form data for populating the edit form, or null if not found
   * @throws Error if projectId is not provided, project is finished, or if loading fails
   */
  async getProjectForEdit(projectId: string): Promise<Partial<ProjectFormData> | null> {
    if (!projectId) {
      throw new Error('Project ID is required')
    }

    try {
      const projectData = await getProject(projectId)

      if (!projectData) {
        return null
      }

      if (projectData.status === 'finished') {
        throw new Error('PROJECT_FINISHED')
      }

      let projectItems
      try {
        const response = await showSelectedItems(projectId)

        if (response?.contents || response?.specificSkills || response?.specific_skills) {
          projectItems = response
        } else if (response?.project_items) {
          projectItems = response.project_items
        } else {
          projectItems = { contents: [], specificSkills: [] }
        }
      } catch (itemsError) {
        console.warn('Could not load selected items:', itemsError)
        projectItems = { contents: [], specificSkills: [] }
      }

      const apiResponse = {
        ...projectData,
        project_items: projectItems
      } as unknown as ProjectApiResponse

      return transformApiResponseToFormData(apiResponse)
    } catch (error) {
      console.error('Error loading project for edit:', error)
      throw error
    }
  }
}

export const projectService = new ProjectService()
